/**
 * Single source of truth for the PAI π mark's SVG geometry.
 *
 * These paths were originally authored inline in `paiLoadingContent.ts` (the
 * loading spinner); they now live here so both the loading screen and the
 * splash-page π mark ({@link ../pi/PiMark}) render byte-identical geometry.
 * See `paiLoadingContent.ts`'s doc comments for how the stroke centerlines
 * were computed from the hand-drawn source image.
 */

/** Stanford cardinal — the π mark's color. */
export const PI_CARDINAL = '#8C1515';

/** SVG viewBox the paths are authored in: `0 0 194 172`. */
export const PI_VIEWBOX = '0 0 194 172';

/**
 * Height ÷ width of {@link PI_VIEWBOX} — multiply a rendered width by this
 * to get the mark's rendered height.
 */
export const PI_ASPECT = 172 / 194;

/**
 * Filled outline of the whole glyph, extracted verbatim from the hand-drawn
 * source. Used as a faint ghost so the target shape stays legible while the
 * stroke centerlines animate over it.
 */
export const PI_FILL =
  'M 84.26 48.50 ' +
  'Q 72.74 49.08 63.32 55.03 ' +
  'C 57.30 58.83 52.04 67.36 49.00 72.98 ' +
  'Q 48.00 74.82 45.50 74.54 ' +
  'C 43.18 74.28 42.53 71.69 42.55 69.72 ' +
  'Q 42.75 51.93 53.61 38.81 ' +
  'C 59.45 31.75 67.01 28.53 75.59 26.88 ' +
  'Q 97.88 22.60 118.65 23.05 ' +
  'Q 138.26 23.48 155.99 25.06 ' +
  'Q 160.44 25.46 165.70 27.31 ' +
  'C 175.27 30.67 176.91 42.28 174.30 50.81 ' +
  'C 173.10 54.74 170.86 56.41 166.77 56.33 ' +
  'C 161.84 56.24 156.53 55.41 146.76 54.22 ' +
  'A 1.17 1.17 0.0 0 0 145.46 55.25 ' +
  'C 144.66 62.26 143.45 73.21 143.10 82.86 ' +
  'Q 142.73 93.15 141.24 108.25 ' +
  'Q 141.18 108.92 141.19 119.75 ' +
  'C 141.19 128.60 143.32 137.84 150.92 142.81 ' +
  'C 156.84 146.69 161.78 138.65 163.74 133.74 ' +
  'C 165.02 130.54 166.79 128.91 170.08 130.17 ' +
  'C 176.07 132.48 175.38 140.12 173.66 145.24 ' +
  'C 168.87 159.47 152.74 168.48 138.33 163.03 ' +
  'C 125.69 158.25 118.50 142.78 117.18 129.81 ' +
  'C 115.60 114.24 116.85 95.08 117.54 79.23 ' +
  'C 117.97 69.23 119.34 59.97 120.57 50.21 ' +
  'A 0.75 0.75 0.0 0 0 120.20 49.47 ' +
  'C 118.38 48.45 116.71 48.50 114.77 48.24 ' +
  'Q 107.13 47.24 99.34 47.46 ' +
  'A 1.04 1.03 0.6 0 0 98.33 48.44 ' +
  'C 98.09 52.70 97.56 56.33 97.37 60.86 ' +
  'Q 96.89 72.06 96.23 87.25 ' +
  'Q 95.63 101.07 93.94 111.47 ' +
  'Q 90.22 134.35 76.10 151.84 ' +
  'C 71.51 157.52 65.15 162.84 57.90 163.70 ' +
  'C 45.88 165.11 40.00 156.46 43.29 145.43 ' +
  'C 45.98 136.41 54.12 131.14 61.47 125.88 ' +
  'C 75.54 115.81 79.64 100.87 81.50 84.50 ' +
  'Q 82.85 72.65 83.88 58.14 ' +
  'C 84.14 54.61 84.71 51.84 84.92 49.18 ' +
  'A 0.63 0.63 0.0 0 0 84.26 48.50 Z';

/** Bar stroke centerline: left tip (lower-left curl) → arch → right tip. */
export const PI_BAR_PATH =
  'M 44.3,68.5 L 44.8,67.1 L 45.4,65.7 L 46.0,64.3 L 46.6,63.0 L 47.2,61.7 ' +
  'L 47.8,60.5 L 48.5,59.3 L 49.2,58.1 L 50.0,56.9 L 50.7,55.8 L 51.5,54.8 ' +
  'L 52.3,53.7 L 53.1,52.7 L 54.0,51.7 L 54.9,50.8 L 55.8,49.9 L 56.7,49.0 ' +
  'L 57.6,48.2 L 58.6,47.3 L 59.6,46.6 L 60.6,45.8 L 61.6,45.1 L 62.6,44.4 ' +
  'L 63.7,43.7 L 64.8,43.1 L 65.9,42.4 L 67.0,41.9 L 68.1,41.3 L 69.2,40.8 ' +
  'L 70.4,40.3 L 71.6,39.8 L 72.8,39.3 L 74.0,38.9 L 75.2,38.5 L 76.4,38.1 ' +
  'L 77.6,37.7 L 78.9,37.4 L 80.2,37.1 L 81.4,36.8 L 82.7,36.5 L 84.0,36.2 ' +
  'L 85.3,36.0 L 86.6,35.8 L 88.0,35.6 L 89.3,35.4 L 90.6,35.3 L 92.0,35.1 ' +
  'L 93.3,35.0 L 94.7,34.9 L 96.0,34.8 L 97.4,34.8 L 98.8,34.7 L 100.2,34.7 ' +
  'L 101.6,34.6 L 102.9,34.6 L 104.3,34.6 L 105.7,34.6 L 107.1,34.7 L 108.5,34.7 ' +
  'L 109.9,34.8 L 111.3,34.8 L 112.7,34.9 L 114.1,35.0 L 115.5,35.1 L 116.9,35.2 ' +
  'L 118.3,35.4 L 119.7,35.5 L 121.1,35.6 L 122.5,35.8 L 123.9,35.9 L 125.2,36.1 ' +
  'L 126.6,36.3 L 128.0,36.4 L 129.3,36.6 L 130.7,36.8 L 132.0,37.0 L 133.4,37.2 ' +
  'L 134.7,37.4 L 136.0,37.6 L 137.4,37.8 L 138.7,38.0 L 140.0,38.2 L 141.2,38.5 ' +
  'L 142.5,38.7 L 143.8,38.9 L 145.1,39.1 L 146.3,39.3 L 147.5,39.6 L 148.7,39.8 ' +
  'L 150.0,40.0 L 151.1,40.2 L 152.3,40.4 L 153.5,40.6 L 154.6,40.8 L 155.8,41.0 ' +
  'L 156.9,41.2 L 158.0,41.4 L 159.1,41.6 L 160.1,41.8';

/** Left leg stroke centerline: bar junction (x≈91) → vertical body → leftward foot. */
export const PI_LEFT_PATH =
  'M 91.2,37.2 L 91.1,38.4 L 91.0,39.6 L 90.9,40.9 L 90.8,42.1 L 90.7,43.4 ' +
  'L 90.6,44.6 L 90.5,45.9 L 90.4,47.1 L 90.4,48.4 L 90.3,49.6 L 90.2,50.9 ' +
  'L 90.1,52.1 L 90.1,53.4 L 90.0,54.7 L 90.0,55.9 L 89.9,57.2 L 89.9,58.5 ' +
  'L 89.8,59.8 L 89.8,61.0 L 89.7,62.3 L 89.7,63.6 L 89.6,64.8 L 89.6,66.1 ' +
  'L 89.5,67.4 L 89.5,68.7 L 89.4,69.9 L 89.3,71.2 L 89.3,72.5 L 89.2,73.8 ' +
  'L 89.2,75.0 L 89.1,76.3 L 89.0,77.6 L 88.9,78.8 L 88.8,80.1 L 88.8,81.3 ' +
  'L 88.7,82.6 L 88.6,83.9 L 88.4,85.1 L 88.3,86.4 L 88.2,87.6 L 88.1,88.8 ' +
  'L 87.9,90.1 L 87.8,91.3 L 87.6,92.6 L 87.5,93.8 L 87.3,95.0 L 87.1,96.2 ' +
  'L 86.9,97.5 L 86.7,98.7 L 86.5,99.9 L 86.3,101.1 L 86.0,102.3 L 85.8,103.5 ' +
  'L 85.5,104.6 L 85.2,105.8 L 84.9,107.0 L 84.6,108.2 L 84.3,109.3 L 84.0,110.5 ' +
  'L 83.6,111.6 L 83.3,112.8 L 82.9,113.9 L 82.5,115.0 L 82.1,116.2 L 81.7,117.3 ' +
  'L 81.2,118.4 L 80.7,119.5 L 80.3,120.6 L 79.8,121.6 L 79.2,122.7 L 78.7,123.8 ' +
  'L 78.1,124.8 L 77.6,125.9 L 77.0,126.9 L 76.3,127.9 L 75.7,128.9 L 75.0,130.0 ' +
  'L 74.3,130.9 L 73.6,131.9 L 72.9,132.9 L 72.1,133.9 L 71.4,134.8 L 70.6,135.8 ' +
  'L 69.7,136.7 L 68.9,137.6 L 68.0,138.5 L 67.1,139.4 L 66.2,140.3 L 65.2,141.2 ' +
  'L 64.3,142.1 L 63.3,142.9 L 62.2,143.8 L 61.2,144.6 L 60.1,145.4 L 58.9,146.2 ' +
  'L 57.8,147.0 L 56.6,147.8 L 55.4,148.5 L 54.2,149.3';

/** Right leg stroke centerline: bar junction (x≈132) → vertical body → rightward foot curl. */
export const PI_RIGHT_PATH =
  'M 131.8,39.0 L 132.0,40.5 L 132.1,42.0 L 132.2,43.4 L 132.3,44.9 L 132.3,46.4 ' +
  'L 132.4,47.9 L 132.4,49.3 L 132.4,50.8 L 132.3,52.3 L 132.3,53.8 L 132.2,55.3 ' +
  'L 132.1,56.8 L 132.0,58.3 L 131.9,59.7 L 131.8,61.2 L 131.7,62.7 L 131.5,64.2 ' +
  'L 131.4,65.7 L 131.2,67.2 L 131.0,68.7 L 130.9,70.2 L 130.7,71.8 L 130.5,73.3 ' +
  'L 130.3,74.8 L 130.1,76.3 L 129.9,77.8 L 129.7,79.3 L 129.5,80.9 L 129.4,82.4 ' +
  'L 129.2,83.9 L 129.0,85.5 L 128.8,87.0 L 128.6,88.5 L 128.5,90.1 L 128.3,91.6 ' +
  'L 128.2,93.2 L 128.1,94.7 L 127.9,96.3 L 127.8,97.9 L 127.7,99.4 L 127.7,101.0 ' +
  'L 127.6,102.6 L 127.6,104.2 L 127.5,105.7 L 127.5,107.3 L 127.5,108.9 L 127.6,110.5 ' +
  'L 127.6,112.1 L 127.7,113.7 L 127.8,115.3 L 128.0,116.9 L 128.1,118.5 L 128.3,120.1 ' +
  'L 128.5,121.7 L 128.8,123.3 L 129.0,124.8 L 129.3,126.3 L 129.7,127.9 L 130.0,129.4 ' +
  'L 130.4,130.8 L 130.8,132.3 L 131.3,133.7 L 131.8,135.0 L 132.3,136.4 L 132.9,137.7 ' +
  'L 133.5,138.9 L 134.1,140.2 L 134.8,141.3 L 135.5,142.4 L 136.2,143.5 L 137.0,144.5 ' +
  'L 137.8,145.5 L 138.7,146.4 L 139.6,147.2 L 140.5,148.0 L 141.5,148.7 L 142.6,149.3 ' +
  'L 143.6,149.9 L 144.7,150.3 L 145.8,150.7 L 147.0,151.0 L 148.2,151.3 L 149.4,151.4 ' +
  'L 150.7,151.4 L 151.9,151.3 L 153.2,151.1 L 154.6,150.8 L 155.9,150.4 L 157.3,149.9 ' +
  'L 158.7,149.3 L 160.1,148.5 L 161.5,147.6 L 163.0,146.6 L 164.5,145.4 L 166.0,144.1 ' +
  'L 167.5,142.7 L 169.0,141.1 L 170.5,139.4 L 172.0,137.5';

/**
 * Dash length used for the draw-on stroke animation — set just above the
 * longest single stroke (the right leg, ~150px in the viewBox's coordinate
 * space) so a full `stroke-dashoffset` sweep hides/reveals any of the three
 * strokes completely.
 */
export const PI_DASH = 155;
